/**
 * External biometric devices / attendance software API configuration (localStorage v1).
 */

import { attendanceService } from "../services/api";
import { evaluateCheckInPunch } from "./attendancePolicyEngine";
import {
  getLocalDayKey,
  readEmployeeWorklog,
  recordAttendanceCheckIn,
  recordAttendanceCheckOut,
} from "./attendancePunchStorage";

export const DEVICE_INTEGRATIONS_STORAGE_KEY =
  "hrms_attendance_device_integrations_v1";
export const DEVICE_INTEGRATIONS_UPDATED_EVENT =
  "hrms-attendance-device-integrations-updated";

const POLL_TICK_MS = 60 * 1000;

let pollTimer = null;
let pollStarted = false;

export function createDeviceId() {
  return `dev_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function getDefaultDeviceIntegration() {
  return {
    id: createDeviceId(),
    name: "",
    type: "biometric",
    enabled: true,
    deviceSerial: "",
    location: "",
    baseUrl: "",
    apiKey: "",
    punchesPath: "/api/punches",
    employeeIdField: "employeeId",
    externalIdField: "externalEmployeeId",
    pollIntervalMinutes: 15,
    employeeIdMap: {},
    lastSyncAt: null,
    lastSyncStatus: null,
    lastSyncMessage: null,
  };
}

export function loadDeviceIntegrations() {
  try {
    const raw = localStorage.getItem(DEVICE_INTEGRATIONS_STORAGE_KEY);
    if (!raw) return { devices: [] };
    const parsed = JSON.parse(raw);
    const devices = Array.isArray(parsed?.devices) ? parsed.devices : [];
    return { devices };
  } catch {
    return { devices: [] };
  }
}

export function saveDeviceIntegrations(data) {
  const devices = Array.isArray(data?.devices) ? data.devices : [];
  const next = { devices };
  localStorage.setItem(DEVICE_INTEGRATIONS_STORAGE_KEY, JSON.stringify(next));
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(DEVICE_INTEGRATIONS_UPDATED_EVENT, { detail: next })
    );
  }
  return next;
}

function resolveHrmsEmployeeId(device, externalOrInternalId) {
  const raw = String(externalOrInternalId ?? "").trim();
  if (!raw) return null;
  const map = device?.employeeIdMap || {};
  if (map[raw]) return String(map[raw]);
  if (/^\d+$/.test(raw)) return raw;
  return null;
}

/**
 * Apply check-in/out from biometric or API — mirrors navbar punch + optional API.
 */
function isCurrentLoggedInEmployee(employeeId) {
  try {
    const current = localStorage.getItem("hrms_current_employee_id");
    if (current && String(current) === String(employeeId)) return true;
    const user = JSON.parse(localStorage.getItem("currentUser") || "null");
    const uid = user?.employeeId ?? user?.employee?.id;
    return uid != null && String(uid) === String(employeeId);
  } catch {
    return false;
  }
}

export async function applyExternalAttendancePunch({
  employeeId,
  eventType,
  timestampMs = Date.now(),
  callApi = true,
}) {
  const empId = String(employeeId ?? "").trim();
  if (!empId) throw new Error("Employee id is required");
  const shouldCallApi = callApi && isCurrentLoggedInEmployee(empId);

  if (eventType === "check_in") {
    const open = readEmployeeWorklog(empId, getLocalDayKey(timestampMs));
    if (open?.inMs && open.outMs == null) {
      return { skipped: true, reason: "Already checked in" };
    }
    const gate = evaluateCheckInPunch({
      employee: { id: empId, employeeId: empId },
      checkInMs: timestampMs,
    });
    recordAttendanceCheckIn(empId, timestampMs);
    if (shouldCallApi) {
      try {
        await attendanceService.checkIn();
      } catch (err) {
        console.warn("External check-in API failed; punch saved locally.", err);
      }
    }
    return {
      applied: true,
      eventType: "check_in",
      employeeId: empId,
      timestampMs,
      policyGate: gate,
      warnings: gate?.warnings || [],
    };
  }

  if (eventType === "check_out") {
    recordAttendanceCheckOut(empId, timestampMs);
    if (shouldCallApi) {
      try {
        await attendanceService.checkOut();
      } catch (err) {
        console.warn("External check-out API failed; punch saved locally.", err);
      }
    }
    return { applied: true, eventType: "check_out", employeeId: empId, timestampMs };
  }

  throw new Error(`Unknown event type: ${eventType}`);
}

function normalizeApiPunchRow(row, device) {
  if (!row || typeof row !== "object") return null;
  const extField = device.externalIdField || "externalEmployeeId";
  const intField = device.employeeIdField || "employeeId";
  const rawEmp = row[intField] ?? row.employee_id ?? row[extField] ?? row.userId;
  const employeeId = resolveHrmsEmployeeId(device, rawEmp);
  if (!employeeId) return null;
  const typeRaw = String(
    row.eventType ?? row.type ?? row.direction ?? row.punchType ?? ""
  ).toLowerCase();
  const eventType =
    typeRaw.includes("out") || typeRaw === "o" ? "check_out" : "check_in";
  const ts = row.timestamp ?? row.time ?? row.punchedAt ?? row.at;
  const timestampMs = ts ? new Date(ts).getTime() : Date.now();
  if (!Number.isFinite(timestampMs)) return null;
  return { employeeId, eventType, timestampMs };
}

/** Poll enabled API integrations and apply punches (best-effort). */
export async function syncApiDeviceIntegration(device) {
  if (!device?.enabled || device.type !== "api") {
    return { ok: false, message: "Device disabled or not API type" };
  }
  const base = String(device.baseUrl || "").trim().replace(/\/$/, "");
  if (!base) {
    return { ok: false, message: "Base URL is required" };
  }
  const path = String(device.punchesPath || "/api/punches").trim();
  const url = `${base}${path.startsWith("/") ? path : `/${path}`}`;
  const headers = { Accept: "application/json" };
  if (device.apiKey) {
    headers.Authorization = `Bearer ${device.apiKey}`;
    headers["X-API-Key"] = device.apiKey;
  }
  try {
    const res = await fetch(url, { headers, method: "GET" });
    if (!res.ok) {
      return { ok: false, message: `HTTP ${res.status}` };
    }
    const body = await res.json();
    const rows = Array.isArray(body)
      ? body
      : body?.punches || body?.data || body?.records || [];
    let applied = 0;
    for (const row of rows) {
      const punch = normalizeApiPunchRow(row, device);
      if (!punch) continue;
      await applyExternalAttendancePunch({
        employeeId: punch.employeeId,
        eventType: punch.eventType,
        timestampMs: punch.timestampMs,
        callApi: false,
      });
      applied += 1;
    }
    return { ok: true, message: `Applied ${applied} punch(es)`, applied };
  } catch (err) {
    return {
      ok: false,
      message: err?.message || "Sync failed",
    };
  }
}

export async function syncAllEnabledApiDevices() {
  const { devices } = loadDeviceIntegrations();
  const results = [];
  for (const device of devices) {
    if (!device.enabled || device.type !== "api") continue;
    const last = device.lastSyncAt || 0;
    const intervalMs = Math.max(1, Number(device.pollIntervalMinutes) || 15) * 60 * 1000;
    if (Date.now() - last < intervalMs) continue;
    const result = await syncApiDeviceIntegration(device);
    const updated = {
      ...device,
      lastSyncAt: Date.now(),
      lastSyncStatus: result.ok ? "ok" : "error",
      lastSyncMessage: result.message,
    };
    results.push({ deviceId: device.id, ...result });
    const all = loadDeviceIntegrations();
    saveDeviceIntegrations({
      devices: all.devices.map((d) => (d.id === device.id ? updated : d)),
    });
  }
  return results;
}

export function startDeviceIntegrationPoller() {
  if (typeof window === "undefined" || pollStarted) return () => {};
  pollStarted = true;
  const tick = () => {
    syncAllEnabledApiDevices().catch(() => {});
  };
  pollTimer = window.setInterval(tick, POLL_TICK_MS);
  tick();
  return () => {
    pollStarted = false;
    if (pollTimer) window.clearInterval(pollTimer);
    pollTimer = null;
  };
}
